import type { BoardField, BoardFilterFieldType, BoardFilterMode, BoardFilterOperator } from '../../board-v3.types';
import { CURRENCY_CODE } from '../../../../enum/constants';

export type FilterEntityMode = 'card' | 'contact' | 'company';

export interface FilterOperator {
  value: BoardFilterOperator;
  label: string;
  requiresValue?: boolean;
  isRange?: boolean;
  isMulti?: boolean;
}

export interface FilterFieldOption extends BoardField {
  filter_type: BoardFilterFieldType;
}

export interface DraftCondition {
  localId: string;
  key: string;
  operator: BoardFilterOperator;
  value: unknown;
  valueTo?: unknown;
}

export const FILTER_BOOLEAN_OPTIONS = [
  { label: 'yes', value: true },
  { label: 'no', value: false },
];

export const FILTER_MODE_OPTIONS: { value: BoardFilterMode; label: string }[] = [
  { value: 'AND', label: 'board_v3_filter.mode_and' },
  { value: 'OR', label: 'board_v3_filter.mode_or' },
];

export const FILTER_OPERATORS: Record<string, FilterOperator[]> = {
  text: [
    { value: 'ilike', label: 'board_v3_filter.operator_contains' },
    { value: 'not_ilike', label: 'board_v3_filter.operator_not_contains' },
    { value: 'eq', label: 'board_v3_filter.operator_equals' },
    { value: 'ne', label: 'board_v3_filter.operator_not_equals' },
    { value: 'starts_with', label: 'board_v3_filter.operator_starts_with' },
    { value: 'ends_with', label: 'board_v3_filter.operator_ends_with' },
    { value: 'is_null', label: 'board_v3_filter.operator_empty', requiresValue: false },
    { value: 'is_not_null', label: 'board_v3_filter.operator_not_empty', requiresValue: false },
  ],
  number: [
    { value: 'eq', label: 'board_v3_filter.operator_equals' },
    { value: 'ne', label: 'board_v3_filter.operator_not_equals' },
    { value: 'gt', label: 'board_v3_filter.operator_gt' },
    { value: 'gte', label: 'board_v3_filter.operator_gte' },
    { value: 'lt', label: 'board_v3_filter.operator_lt' },
    { value: 'lte', label: 'board_v3_filter.operator_lte' },
    { value: 'between', label: 'board_v3_filter.operator_between', isRange: true },
    { value: 'is_null', label: 'board_v3_filter.operator_empty', requiresValue: false },
    { value: 'is_not_null', label: 'board_v3_filter.operator_not_empty', requiresValue: false },
  ],
  date: [
    { value: 'eq', label: 'board_v3_filter.operator_equals' },
    { value: 'gt', label: 'board_v3_filter.operator_after' },
    { value: 'lt', label: 'board_v3_filter.operator_before' },
    { value: 'between', label: 'board_v3_filter.operator_between', isRange: true },
    { value: 'is_null', label: 'board_v3_filter.operator_empty', requiresValue: false },
    { value: 'is_not_null', label: 'board_v3_filter.operator_not_empty', requiresValue: false },
  ],
  select: [
    { value: 'eq', label: 'board_v3_filter.operator_equals' },
    { value: 'ne', label: 'board_v3_filter.operator_not_equals' },
    { value: 'in', label: 'board_v3_filter.operator_in', isMulti: true },
    { value: 'not_in', label: 'board_v3_filter.operator_not_in', isMulti: true },
    { value: 'is_null', label: 'board_v3_filter.operator_empty', requiresValue: false },
    { value: 'is_not_null', label: 'board_v3_filter.operator_not_empty', requiresValue: false },
  ],
  boolean: [
    { value: 'eq', label: 'board_v3_filter.operator_is' },
    { value: 'ne', label: 'board_v3_filter.operator_is_not' },
    { value: 'is_null', label: 'board_v3_filter.operator_empty', requiresValue: false },
    { value: 'is_not_null', label: 'board_v3_filter.operator_not_empty', requiresValue: false },
  ],
  manager: [
    { value: 'eq', label: 'board_v3_filter.operator_equals' },
    { value: 'ne', label: 'board_v3_filter.operator_not_equals' },
    { value: 'in', label: 'board_v3_filter.operator_in', isMulti: true },
    { value: 'not_in', label: 'board_v3_filter.operator_not_in', isMulti: true },
    { value: 'is_null', label: 'board_v3_filter.operator_empty', requiresValue: false },
    { value: 'is_not_null', label: 'board_v3_filter.operator_not_empty', requiresValue: false },
  ],
};

export function getSystemFilterFields(entityMode: FilterEntityMode): FilterFieldOption[] {
  if (entityMode === 'company') {
    return [
      createSystemField('name', 'company', 'text', 'apartment'),
      createSystemField('company_details', 'board_v3_company_filter.company_details', 'text', 'search'),
      createSystemField('phone', 'phone', 'text', 'mobile'),
      createSystemField('email', 'email', 'text', 'alternate_email'),
      createSystemField('address', 'addresses', 'text', 'location_on'),
      createSystemField('created_at', 'created_at', 'date', 'event'),
      createSystemField('updated_at', 'updated_at', 'date', 'update'),
      createSystemField('created_by', 'board_v3_company_filter.created_by', 'manager', 'person'),
      createSystemField('updated_by', 'updated_by', 'manager', 'person'),
      createSystemField('card_name', 'board_v3_company_filter.card_name', 'text', 'view_kanban'),
      createSystemField('cards_count', 'board_v3_company_filter.cards_count', 'number', 'numbers'),
      createSystemField('funnel_name', 'board_v3_company_filter.funnel_name', 'text', 'filter_alt'),
      createSystemField('column_name', 'board_v3_company_filter.column_name', 'text', 'view_column'),
      createSystemField('assigned_id', 'board_v3_company_filter.assigned_id', 'manager', 'person'),
    ];
  }
  if (entityMode === 'contact') {
    return [
      createSystemField('first_name', 'first_name', 'text', 'person'),
      createSystemField('last_name', 'last_name', 'text', 'person'),
      createSystemField('middle_name', 'middle_name', 'text', 'person'),
      createSystemField('phone', 'phone', 'text', 'mobile'),
      createSystemField('email', 'email', 'text', 'alternate_email'),
      createSystemField('created_at', 'created_at', 'date', 'event'),
      createSystemField('updated_at', 'updated_at', 'date', 'update'),
      createSystemField('created_by', 'board_v3_contact_filter.created_by', 'manager', 'person'),
      createSystemField('updated_by', 'updated_by', 'manager', 'person'),
      createSystemField('card_name', 'board_v3_contact_filter.card_name', 'text', 'view_kanban'),
      createSystemField('cards_count', 'board_v3_contact_filter.cards_count', 'number', 'numbers'),
      createSystemField('funnel_name', 'board_v3_contact_filter.funnel_name', 'text', 'filter_alt'),
      createSystemField('column_name', 'board_v3_contact_filter.column_name', 'text', 'view_column'),
      createSystemField('assigned_id', 'board_v3_contact_filter.assigned_id', 'manager', 'person'),
    ];
  }
  return [
    createSystemField('name', 'custom_filters.name', 'text', 'text_fields'),
    createSystemField('description', 'description', 'text', 'notes'),
    createSystemField('price', 'custom_filters.price', 'number', 'attach_money'),
    createSystemField('currency', 'board_v3_field_currency', 'select', 'payments'),
    createSystemField('assigned_id', 'custom_filters.assigned_id', 'manager', 'person'),
    createSystemField('phone', 'phone', 'text', 'mobile'),
    createSystemField('email', 'email', 'text', 'alternate_email'),
    createSystemField('comment', 'comment', 'text', 'comment'),
    createSystemField('is_unread', 'custom_filters.is_unread', 'boolean', 'mark_chat_unread'),
    createSystemField('created_at', 'created_at', 'date', 'event'),
    createSystemField('updated_at', 'updated_at', 'date', 'update'),
  ];
}

function createSystemField(
  identifier: string,
  name: string,
  filterType: BoardFilterFieldType,
  icon: string,
): FilterFieldOption {
  return {
    id: identifier,
    identifier,
    name,
    input_type: filterType,
    filter_type: filterType,
    position: 0,
    icon,
    is_system: true,
    options: identifier === 'currency' ? CURRENCY_CODE.map((currency) => String(currency.value)) : [],
  };
}

export function filterFieldKey(field: Pick<BoardField, 'identifier' | 'id' | 'name'>): string {
  return String(field.identifier ?? field.id ?? field.name ?? '');
}

export function toFilterFieldType(field: BoardField): BoardFilterFieldType {
  if (field.identifier === 'assigned_id') return 'manager';
  if (['number', 'price'].includes(field.input_type)) return 'number';
  if (field.input_type === 'date') return 'date';
  if (field.input_type === 'select') return 'select';
  if (field.input_type === 'checkbox') return 'boolean';
  return 'text';
}

export function operatorsForField(field: FilterFieldOption): FilterOperator[] {
  return FILTER_OPERATORS[field.filter_type] ?? FILTER_OPERATORS['text'];
}
